/**
 * InstructionStepOne Component (Dual-Mode Support)
 *
 * Simplified study instructions with mode-specific content.
 * Removes redundant info already covered in Consent form.
 *
 * DYAD mode:
 * - Shows study flow with partner interaction
 * - Critical partner warning (dropout affects both)
 * - 3 attention check questions about roles
 *
 * SINGLE mode:
 * - Shows simplified study flow (no partner)
 * - Simple commitment note
 * - 1 attention check question
 */

import React, { useState } from "react";
import { usePlayer, useGame } from "@empirica/core/player/classic/react";

// Attention check question (same for both modes)
const attentionCheckQuestions = [
  {
    id: 1,
    question: "What does this study require from you?",
    options: [
      "A. Casual browsing while multitasking",
      "B. Full attention to learn unfamiliar academic material",
      "C. No preparation, just answer random questions"
    ],
    correct: "B"
  }
];

export function InstructionStepOne({ next }) {
  const player = usePlayer();
  const game = useGame();
  const treatment = game?.get("treatment") || {};
  const gameMode = treatment.gameMode || "dyad";
  const isSingleMode = gameMode === "single";

  const [readChecked, setReadChecked] = useState(false);
  const [showAttentionCheck, setShowAttentionCheck] = useState(false);

  // Attention check states
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [allCorrect, setAllCorrect] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [startTime] = useState(Date.now());

  // Same attention question for both single and dyad modes
  const attentionQuestions = attentionCheckQuestions;

  const handleCheckboxChange = (checked) => {
    setReadChecked(checked);
    if (checked) {
      setShowAttentionCheck(true);
    } else {
      setShowAttentionCheck(false);
      setAnswers({});
      setSubmitted(false);
      setAllCorrect(false);
    }
  };

  const handleAnswerChange = (questionId, value) => {
    setAnswers(prevState => ({
      ...prevState,
      [`q${questionId}`]: value,
    }));
    setSubmitted(false);
  };

  const handleSubmit = () => {
    const allAnswered = Object.keys(answers).length === attentionQuestions.length;

    if (!allAnswered) {
      setSubmitted(true);
      setAllCorrect(false);
      return;
    }

    const correct = attentionQuestions.every(q =>
      answers[`q${q.id}`] === q.correct
    );

    setAttempts(prev => prev + 1);
    setSubmitted(true);
    setAllCorrect(correct);

    if (correct) {
      const timeToComplete = Math.round((Date.now() - startTime) / 1000);
      player.set("attention_check_attempts", attempts + 1);
      player.set("attention_check_time", timeToComplete);

      setTimeout(() => {
        next();
      }, 1000);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center p-4 sm:p-8">
      <div className="w-full max-w-3xl">
        {/* Main Card */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Header with gradient */}
          <div className="bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-700 px-8 py-6 text-white">
            <div className="flex items-center gap-4 mb-3">
              <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center">
                <MapIcon className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold">Study Flow</h1>
                <p className="text-blue-100 text-sm mt-1">
                  {isSingleMode ? "Self-paced Learning" : "Collaborative Learning"}
                </p>
              </div>
            </div>
            <p className="text-blue-100 text-sm leading-relaxed">
              {isSingleMode
                ? "Here's what you'll do in this study"
                : "Here's how the study works with your partner"}
            </p>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-3 divide-x divide-gray-100 bg-gray-50 border-b border-gray-100">
            <QuickStat
              icon={<ClockIcon />}
              label="Duration"
              value={isSingleMode ? "15-20 min" : "30-35 min"}
            />
            <QuickStat
              icon={<StepsIcon />}
              label="Steps"
              value={isSingleMode ? "3 phases" : "4 phases"}
            />
            <QuickStat
              icon={<TargetIcon />}
              label="Goal"
              value="Learn & Quiz"
            />
          </div>

          {/* Content */}
          <div className="p-6 sm:p-8">
            {/* Attention Required Notice */}
            <div className="mb-6 p-4 bg-purple-50 rounded-xl border-2 border-purple-200">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                  <BrainIcon className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-bold text-purple-800 mb-1">This Study Requires Your Full Attention</h3>
                  <p className="text-sm text-purple-700">
                    You will learn <strong>new and unfamiliar material</strong> on an academic topic.
                    Please find a quiet environment, minimize distractions, and be prepared to
                    <strong> actively engage</strong> with the content. You will be tested on what you learn.
                  </p>
                </div>
              </div>
            </div>

            {/* Role Assignment Info - DYAD only */}
            {!isSingleMode && (
              <div className="mb-6 p-4 bg-indigo-50 rounded-xl border border-indigo-200">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
                    <UsersIcon className="w-4 h-4 text-indigo-600" />
                  </div>
                  <p className="text-sm text-indigo-800">
                    You will be randomly assigned as either <strong>Teacher</strong> (studies materials, then teaches)
                    or <strong>Student</strong> (learns from the Teacher via chat).
                  </p>
                </div>
              </div>
            )}

            {/* Study Flow Steps */}
            <div className="mb-6">
              <h2 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <ListIcon className="w-5 h-5 text-blue-600" />
                Study Phases
              </h2>

              <div className="space-y-3">
                {isSingleMode ? (
                  <>
                    <FlowStep
                      number={1}
                      title="Familiarity Survey"
                      desc="Rate your knowledge of different topics"
                      icon={<SurveyIcon />}
                      color="blue"
                    />
                    <FlowStep
                      number={2}
                      title="Learning Phase"
                      desc="Study the assigned topic using provided materials"
                      icon={<BookIcon />}
                      color="indigo"
                    />
                    <FlowStep
                      number={3}
                      title="Comprehension Quiz"
                      desc="Answer questions about what you learned"
                      icon={<QuizIcon />}
                      color="violet"
                    />
                  </>
                ) : (
                  <>
                    <FlowStep
                      number={1}
                      title="Familiarity Survey"
                      desc="Rate your knowledge of different topics"
                      icon={<SurveyIcon />}
                      color="blue"
                    />
                    <FlowStep
                      number={2}
                      title="Learning Phase"
                      desc="Teacher studies materials; Student reads a different topic"
                      icon={<BookIcon />}
                      color="indigo"
                    />
                    <FlowStep
                      number={3}
                      title="Chat Session"
                      desc="Teacher explains the topic to Student"
                      time="10 min"
                      icon={<ChatIcon />}
                      color="violet"
                    />
                    <FlowStep
                      number={4}
                      title="Comprehension Quiz"
                      desc="Both participants answer questions about the topic"
                      icon={<QuizIcon />}
                      color="purple"
                    />
                  </>
                )}
              </div>
            </div>

            {/* Warning/Commitment Section */}
            {isSingleMode ? (
              <div className="mb-6 p-4 bg-amber-50 rounded-xl border border-amber-200">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                    <NoteIcon className="w-4 h-4 text-amber-600" />
                  </div>
                  <p className="text-sm text-amber-800">
                    <strong>Please complete the study in one sitting.</strong> Ensure you have 15-20 minutes available and a stable internet connection.
                  </p>
                </div>
              </div>
            ) : (
              <div className="mb-6 p-4 bg-red-50 rounded-xl border-2 border-red-200">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
                    <AlertIcon className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-red-800 mb-1">Your Partner is Counting on You</h3>
                    <p className="text-sm text-gray-700">
                      Once matched, another participant depends on you to complete the study.
                    </p>
                  </div>
                </div>
                <div className="ml-13 pl-11">
                  <ul className="text-sm text-gray-700 space-y-1 mb-3">
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-red-400"></span>
                      If you leave early, your partner's session is <strong>cancelled</strong>
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-red-400"></span>
                      <strong>Neither of you</strong> will receive payment
                    </li>
                  </ul>
                  <p className="text-sm text-red-700 font-medium bg-red-100 p-2 rounded-lg">
                    Only continue if you have 30-35 minutes available right now.
                  </p>
                </div>
              </div>
            )}

            {/* Confirmation Checkbox */}
            <div className="mb-6">
              <label
                className={`flex items-center gap-3 p-4 rounded-xl cursor-pointer transition-all duration-200 ${
                  readChecked
                    ? "bg-emerald-50 border-2 border-emerald-300"
                    : "bg-gray-50 border-2 border-transparent hover:bg-gray-100"
                }`}
              >
                <div className="relative flex-shrink-0">
                  <input
                    type="checkbox"
                    checked={readChecked}
                    onChange={(e) => handleCheckboxChange(e.target.checked)}
                    className="sr-only"
                  />
                  <div
                    className={`w-6 h-6 rounded-md flex items-center justify-center transition-all duration-200 ${
                      readChecked
                        ? "bg-emerald-600"
                        : "bg-white border-2 border-gray-300"
                    }`}
                  >
                    {readChecked && (
                      <svg className="w-4 h-4 text-white" viewBox="0 0 12 12" fill="none">
                        <path
                          d="M2 6L5 9L10 3"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </div>
                </div>
                <span className={`font-medium transition-colors duration-200 ${
                  readChecked ? "text-emerald-800" : "text-gray-700"
                }`}>
                  I understand and am ready to proceed
                </span>
              </label>
            </div>

            {/* Attention Check */}
            {showAttentionCheck && (
              <div className="border-t border-gray-200 pt-6">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                    <ChecklistIcon className="w-4 h-4 text-blue-600" />
                  </div>
                  <h2 className="text-lg font-bold text-gray-900">Quick Check</h2>
                </div>
                <p className="mb-4 text-sm text-gray-600">
                  Please answer this question to continue.
                </p>

                <div className="space-y-4">
                  {attentionQuestions.map((q) => (
                    <AttentionQuestion
                      key={q.id}
                      question={q}
                      currentAnswer={answers[`q${q.id}`]}
                      onAnswerChange={(value) => handleAnswerChange(q.id, value)}
                      showError={submitted && !allCorrect && answers[`q${q.id}`] !== q.correct}
                      disabled={submitted && allCorrect}
                    />
                  ))}
                </div>

                {/* Feedback Messages */}
                {submitted && !allCorrect && (
                  <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
                      <XIcon className="w-4 h-4 text-red-600" />
                    </div>
                    <p className="text-sm text-red-700">
                      Incorrect. Please try again.
                    </p>
                  </div>
                )}

                {submitted && allCorrect && (
                  <div className="mt-4 p-4 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                      <CheckIcon className="w-4 h-4 text-emerald-600" />
                    </div>
                    <p className="text-sm text-emerald-700 font-medium">
                      Correct! Proceeding to the next step...
                    </p>
                  </div>
                )}

                {/* Submit Button */}
                <div className="mt-6">
                  <button
                    onClick={handleSubmit}
                    disabled={submitted && allCorrect}
                    className={`w-full py-4 px-6 rounded-xl font-semibold text-lg transition-all duration-300 ${
                      submitted && allCorrect
                        ? "bg-emerald-500 text-white cursor-not-allowed"
                        : "bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                    }`}
                  >
                    {submitted && allCorrect ? (
                      <span className="flex items-center justify-center gap-2">
                        <LoadingSpinner />
                        Proceeding...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        <CheckCircleIcon className="w-5 h-5" />
                        Continue
                      </span>
                    )}
                  </button>
                </div>

                {attempts > 1 && !allCorrect && (
                  <p className="mt-3 text-xs text-gray-500 text-center">
                    Attempts: {attempts}
                  </p>
                )}
              </div>
            )}

            {/* Prompt to check box */}
            {!showAttentionCheck && (
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <ArrowUpIcon className="w-4 h-4 text-blue-600" />
                </div>
                <p className="text-sm text-blue-700">
                  Check the box above to continue to the quick check.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Flow Step Component
function FlowStep({ number, title, desc, time, icon, color = "blue" }) {
  const colorClasses = {
    blue: "bg-blue-500",
    indigo: "bg-indigo-500",
    violet: "bg-violet-500",
    purple: "bg-purple-500",
  };

  const iconBgClasses = {
    blue: "bg-blue-100 text-blue-600",
    indigo: "bg-indigo-100 text-indigo-600",
    violet: "bg-violet-100 text-violet-600",
    purple: "bg-purple-100 text-purple-600",
  };

  return (
    <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl border border-gray-100 hover:border-gray-200 transition-colors">
      <div className={`w-10 h-10 rounded-xl ${colorClasses[color]} text-white flex items-center justify-center text-lg font-bold flex-shrink-0 shadow-sm`}>
        {number}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <h3 className="font-semibold text-gray-900">{title}</h3>
          {time && (
            <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full font-medium">
              {time}
            </span>
          )}
        </div>
        <p className="text-sm text-gray-600">{desc}</p>
      </div>
      <div className={`w-8 h-8 rounded-lg ${iconBgClasses[color]} flex items-center justify-center flex-shrink-0`}>
        {icon}
      </div>
    </div>
  );
}

// Attention Question Component
function AttentionQuestion({ question, currentAnswer, onAnswerChange, showError, disabled }) {
  return (
    <div className={`p-4 rounded-xl border-2 transition-all ${
      showError ? "bg-red-50 border-red-300" : "bg-white border-gray-200"
    }`}>
      <h3 className="font-semibold text-gray-900 mb-3">
        {question.id}. {question.question}
      </h3>
      <div className="space-y-2">
        {question.options.map((option, index) => {
          const value = option.charAt(0);
          const isSelected = currentAnswer === value;

          return (
            <label
              key={index}
              className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all ${
                disabled ? "opacity-60 cursor-not-allowed" : ""
              } ${
                isSelected
                  ? "bg-blue-50 border border-blue-200"
                  : "bg-gray-50 border border-transparent hover:bg-gray-100"
              }`}
            >
              <div className="relative flex-shrink-0">
                <input
                  type="radio"
                  name={`q${question.id}`}
                  value={value}
                  checked={isSelected}
                  onChange={() => onAnswerChange(value)}
                  disabled={disabled}
                  className="sr-only"
                />
                <div
                  className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                    isSelected
                      ? "border-blue-600 bg-blue-600"
                      : "border-gray-300 bg-white"
                  }`}
                >
                  {isSelected && (
                    <div className="w-2 h-2 rounded-full bg-white"></div>
                  )}
                </div>
              </div>
              <span className={`text-sm ${isSelected ? "text-blue-900 font-medium" : "text-gray-700"}`}>
                {option}
              </span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

// Quick Stat Component
function QuickStat({ icon, label, value }) {
  return (
    <div className="py-4 px-3 text-center">
      <div className="w-8 h-8 mx-auto mb-2 rounded-lg bg-blue-100 flex items-center justify-center text-blue-600">
        {icon}
      </div>
      <p className="text-xs text-gray-500 mb-0.5">{label}</p>
      <p className="text-sm font-semibold text-gray-900">{value}</p>
    </div>
  );
}

// Loading Spinner
function LoadingSpinner() {
  return (
    <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24" fill="none">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
    </svg>
  );
}

// Icons
function MapIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 6.75V15m6-6v8.25m.503 3.498l4.875-2.437c.381-.19.622-.58.622-1.006V4.82c0-.836-.88-1.38-1.628-1.006l-3.869 1.934c-.317.159-.69.159-1.006 0L9.503 3.252a1.125 1.125 0 00-1.006 0L3.622 5.689C3.24 5.88 3 6.27 3 6.695V19.18c0 .836.88 1.38 1.628 1.006l3.869-1.934c.317-.159.69-.159 1.006 0l4.994 2.497c.317.158.69.158 1.006 0z" />
    </svg>
  );
}

function BrainIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function StepsIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 12h16.5m-16.5 3.75h16.5M3.75 19.5h16.5M5.625 4.5h12.75a1.875 1.875 0 010 3.75H5.625a1.875 1.875 0 010-3.75z" />
    </svg>
  );
}

function TargetIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9 9 0 100-18 9 9 0 000 18z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 15a3 3 0 100-6 3 3 0 000 6z" />
    </svg>
  );
}

function UsersIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
    </svg>
  );
}

function ListIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 6.75h12M8.25 12h12m-12 5.25h12M3.75 6.75h.007v.008H3.75V6.75zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zM3.75 12h.007v.008H3.75V12zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm-.375 5.25h.007v.008H3.75v-.008zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
    </svg>
  );
}

function SurveyIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z" />
    </svg>
  );
}

function BookIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
    </svg>
  );
}

function ChatIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 8.511c.884.284 1.5 1.128 1.5 2.097v4.286c0 1.136-.847 2.1-1.98 2.193-.34.027-.68.052-1.02.072v3.091l-3-3c-1.354 0-2.694-.055-4.02-.163a2.115 2.115 0 01-.825-.242m9.345-8.334a2.126 2.126 0 00-.476-.095 48.64 48.64 0 00-8.048 0c-1.131.094-1.976 1.057-1.976 2.192v4.286c0 .837.46 1.58 1.155 1.951m9.345-8.334V6.637c0-1.621-1.152-3.026-2.76-3.235A48.455 48.455 0 0011.25 3c-2.115 0-4.198.137-6.24.402-1.608.209-2.76 1.614-2.76 3.235v6.226c0 1.621 1.152 3.026 2.76 3.235.577.075 1.157.14 1.74.194V21l4.155-4.155" />
    </svg>
  );
}

function QuizIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z" />
    </svg>
  );
}

function NoteIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
    </svg>
  );
}

function AlertIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
    </svg>
  );
}

function ChecklistIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M11.35 3.836c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m8.9-4.414c.376.023.75.05 1.124.08 1.131.094 1.976 1.057 1.976 2.192V16.5A2.25 2.25 0 0118 18.75h-2.25m-7.5-10.5H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V18.75m-7.5-10.5h6.375c.621 0 1.125.504 1.125 1.125v9.375m-8.25-3l1.5 1.5 3-3.75" />
    </svg>
  );
}

function ArrowUpIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
    </svg>
  );
}

function XIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

function CheckIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
    </svg>
  );
}

function CheckCircleIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}
